import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type { DeclaredHoliday, Holiday, HolidayListParams } from '@/types/holiday';
import type { HolidayPayload } from '../schemas/holiday.schema';

/**
 * Every call this feature makes. As with the rest of the institute area there is
 * no institute id in any path — the server takes it from the caller's token, so
 * there is nothing for a client to point at another institute's calendar.
 *
 * The academic year is the server's decision too: a holiday is always declared
 * against the institute's current session.
 */
export const holidaysApi = {
  list: (params: HolidayListParams = {}) =>
    unwrap(
      apiClient.get<ApiSuccess<Holiday[]>>('/institute/holidays', {
        // Empty filters are dropped rather than sent as blanks — the server
        // validates the shape of what arrives, and `type=` is not a type.
        params: {
          ...(params.month && { month: params.month }),
          ...(params.type && { type: params.type }),
        },
      }),
    ),

  /** `notifyParentsBySms` is a declaration-only choice, so it rides beside the payload. */
  create: (payload: HolidayPayload, notifyParentsBySms = false) =>
    unwrap(
      apiClient.post<ApiSuccess<DeclaredHoliday>>('/institute/holidays', {
        ...payload,
        notifyParentsBySms,
      }),
    ),

  update: (uuid: string, payload: HolidayPayload) =>
    unwrap(apiClient.patch<ApiSuccess<Holiday>>(`/institute/holidays/${uuid}`, payload)),

  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/holidays/${uuid}`)),
};
